/* eslint-disable @typescript-eslint/no-explicit-any */
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Button,
  ButtonProps,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import ImportFileUploader from 'components/ui/file-uploader/ImportFileUploader';
import { IsSuperAdmin } from 'shared/permissions/IsSuperAdmin';
import { useIsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import toast from 'shared/utils/toast';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToolbarAction } from '../../types/toolbar.types';

const buttonProps: ButtonProps = {
  size: 'medium'
};

interface TableActionSectionProps {
  service: any;
  outerParams?: Record<string, unknown>;
  entityId?: number;
  disableExport?: boolean;
  actions: ToolbarAction[];
}

export const TableActionSection = ({
  service,
  outerParams,
  entityId,
  disableExport,
  actions
}: TableActionSectionProps) => {
  const isAdmin = useIsTenantAdministrator();
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeActionId, setActiveActionId] = useState<string | null>(null);

  const handleOpenDropdown = (event: React.MouseEvent<HTMLElement>, actionId: string) => {
    setAnchorEl(event.currentTarget);
    setActiveActionId(actionId);
  };

  const handleCloseDropdown = () => {
    setAnchorEl(null);
    setActiveActionId(null);
  };

  const exportAll = () => {
    if (!service?.exportAll) return;
    service.exportAll(outerParams || {});
    toast.info(t('messages.export-required'));
  };

  const exportSummary = () => {
    if (!service?.exportSummary) return;
    service.exportSummary(outerParams || {});
    toast.info(t('messages.export-required'));
  };

  const handleActionClick = (event: React.MouseEvent<HTMLElement>, action: ToolbarAction) => {
    //  Se è definito un onClick custom nel componente padre, esegui quello
    if (action.onClick) {
      action.onClick(event);
      return;
    }
  };

  // Generazione dinamica delle azioni di esportazione Server-Side (Agnostiche rispetto alla tabella)
  const defaultActions: ToolbarAction[] = [];

  if (isAdmin && !disableExport) {
    if (service?.exportAll) {
      defaultActions.push({
        id: 'export-all',
        label: t('data-grid.export-all'),
        onClick: exportAll,
        actionType: 'button',
        variant: 'contained',
        color: 'success'
      });
    }
    if (service?.exportSummary) {
      defaultActions.push({
        id: 'export-summary',
        label: t('data-grid.export-summary'),
        onClick: exportSummary,
        actionType: 'button',
        variant: 'contained',
        color: 'success'
      });
    }
  }

  const mergedActions = [...defaultActions, ...actions];
  const visibleActions = mergedActions.filter((action) => action.visible !== false);

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ flexWrap: 'nowrap', flexShrink: 0, marginLeft: 'auto' }}>
      {/* Modulo File Uploader protetto da autorizzazione SuperAdmin */}
      <IsSuperAdmin>
        {(!!service.importFromFile || !!service.importFromFileDetail) && (
          <ImportFileUploader service={service} entityId={entityId} />
        )}
      </IsSuperAdmin>

      {visibleActions.map((action) => {
        const type = action.actionType || 'button';

        if (type === 'iconButton') {
          return (
            <Tooltip key={action.id} title={action.label} arrow placement="top">
              <span>
                <IconButton
                  {...buttonProps}
                  disabled={action.disabled}
                  onClick={action.onClick}
                  sx={{
                    width: 40,
                    height: 40,
                    border: action.variant === 'outlined' ? '1px solid' : 'none',
                    borderColor: 'divider',
                    borderRadius: 2
                  }}>
                  {action.startIcon}
                </IconButton>
              </span>
            </Tooltip>
          );
        }

        if (type === 'dropdown') {
          const isCurrentDropdownOpen = anchorEl !== null && activeActionId === action.id;

          return (
            <React.Fragment key={action.id}>
              <Button
                {...buttonProps}
                variant={action.variant || 'text'}
                color={action.color || 'primary'}
                startIcon={action.startIcon}
                endIcon={<KeyboardArrowDownIcon />}
                disabled={action.disabled}
                onClick={(e) => handleOpenDropdown(e, action.id)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  height: '40px',
                  whiteSpace: 'nowrap'
                }}>
                {action.label}
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={isCurrentDropdownOpen}
                onClose={handleCloseDropdown}
                PaperProps={{ sx: { borderRadius: 2, minWidth: 160, mt: 0.5 } }}>
                {action.dropdownItems?.map((item) => (
                  <MenuItem
                    key={item.id}
                    onClick={(e) => {
                      handleCloseDropdown();
                      handleActionClick(e, item);
                    }}
                    disabled={item.disabled}
                    sx={{ typography: 'body2', py: 1, px: 2 }}>
                    {item.icon && (
                      <ListItemIcon sx={{ minWidth: '32px !important' }}>{item.icon}</ListItemIcon>
                    )}
                    <ListItemText primary={item.label} />
                  </MenuItem>
                ))}
              </Menu>
            </React.Fragment>
          );
        }

        // Default CASE: Standard Text Button
        return (
          <Button
            key={action.id}
            {...buttonProps}
            variant={action.variant || 'text'}
            color={action.color}
            startIcon={action.startIcon}
            disabled={action.disabled}
            onClick={action.onClick}
            sx={{
              textTransform: 'none',
              backgroundColor: action.variant === 'text' ? 'none' : '#1d70b8',
              fontWeight: 600,
              fontSize: '0.875rem',
              borderRadius: action.variant === 'text' ? 0 : 2,
              px: action.variant === 'text' ? 1 : 2.5,
              height: '40px',
              boxShadow: 'none',
              whiteSpace: 'nowrap',
              minWidth: 'max-content',
              '& .MuiButton-startIcon': {
                color: action.variant === 'text' ? '#006DB0' : '#ffffff',
                marginRight: '6px'
              },
              '&:hover': {
                boxShadow: 'none',
                backgroundColor: action.variant === 'text' ? 'rgba(0, 109, 176, 0.04)' : '#155a96',
                textDecoration: action.variant === 'text' ? 'underline' : 'none'
              }
            }}>
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 500,
                fontSize: '13px',
                lineHeight: '1.2', // 120%
                letterSpacing: '0em',
                textTransform: 'none',
                color: action.variant === 'text' ? '#006DB0' : '#ffffff'
              }}>
              {action.label}
            </Typography>
          </Button>
        );
      })}
    </Stack>
  );
};
